import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CaseFormModel } from 'app/models/case-form.model';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { FormFactoryService } from './form-factory.service';
import { CreateCaseRequestModel } from 'app/models/create-case-request.model';
import { AlertMessagesConfig } from 'config/alert-messages.config';
import { SaveCaseRequestModel } from 'app/models/save-case-request.model';
import { CaseResponseModel } from 'app/models/case-response.model';
import { RetrieveCaseByFiltersRequestModel } from 'app/models/retrieve-case-by-filters-request.model';
import { ApplicationInfoModel } from 'app/models/application-info.model';
import { CreateCaseResponseModel } from 'app/models/create-case-response.model';
import { FormConstants } from 'app/constants/form.constants';
import { UserService } from 'app/users/user.service';
import { FetchAttachmentsRequest } from 'app/models/fetch-attachments-request.model';
import { AttachmentModel } from 'app/models/attachment.model';
import { AllCaseAttachmentsModel } from 'app/models/all-case-attachments.model';
import { DocumentConstants } from 'app/constants/document.constants';
import { IAppFormLink } from 'app/applications/app-form-link';
import { Router } from '@angular/router';
import { ResultResponse } from 'app/models/common.model';
import { UnlockCaseModel } from 'app/models/unlock-case.model';

export type UsanFormResponse = {
  data: CaseFormModel
}

export type CaseResponse = {
  Data: CaseResponseModel,
  Errors: [],
  ErrorMessage: string
}

export type CreateCaseResponse = {
  Data: CreateCaseResult,
  Errors: [],
  ErrorMessage: string
}

export type CreateCaseResult = {
  result: CreateCaseResponseModel,
}

export type SaveCaseResponse = {
  Data: RetrieveCaseResultResponse, 
}

export type RetrieveCaseResultResponse = {
  result: CaseResponseModel
}

export type FetchAttachmentsResponse = {
  Data: FetchAttachmentsResult
  Errors: [],
  ErrorMessage: string
}

export type FetchAttachmentsResult = {
  result: AttachmentModel[],
}

export type RemoveAttachmentResponse = {
  Data: string,
  Errors: [],
  ErrorMessage: string
}

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private apiUrl = 'usanapi';
  private loadingForm: boolean = false;
  private currentUsanForm!: FormGroup;
  private currentUsanFormSubject = new Subject<any>();
  private showingErrorSections: boolean = false;
  public invalidFields: string[] = [];
  private invalidFormFieldsSubject = new Subject<string[]>();
  private allCaseAttachments!: AllCaseAttachmentsModel;
  private allCaseAttachmentsSubject = new Subject<any>();

  constructor(
    private formFactoryService: FormFactoryService, 
    private http: HttpClient, 
    private router: Router,
    public alertsMessageConfig: AlertMessagesConfig, 
    private userService: UserService
  ) { }

  public createUsanForm(formType: string): FormGroup {
    formType = this.convertFormTypeForUrl(formType);
    let form = this.formFactoryService.createCaseFormGroup(undefined, formType);
    this.hideErrorSections();
    this.sendUsanFormUpdatedMessage(form);
    this.sendAllCaseAttachmentsUpdatedMessage(new AllCaseAttachmentsModel());
    return form;
  }

  public updateUsanFormValue(propertyName: string, value: any) {
    this.getCurrentUsanFormValue().patchValue({ [propertyName] : value});
    this.sendUsanFormUpdatedMessage(this.getCurrentUsanFormValue());
  } 

  private convertFormTypeForUrl(formType?: string): string {
    if(!formType) {
      return '';
    }
    const formTypeParam = FormConstants.FORM_TYPE_TRANSLATION_MAP.get(formType);
    if(formTypeParam) {
      return formTypeParam;
    }

    return formType;
  }

  public sendUsanFormUpdatedMessage(form: FormGroup) {
    if(form === null || form === undefined) {
      return;
    }
    this.currentUsanForm = form;
    this.currentUsanFormSubject.next({ value: form });
  }

  getCurrentUsanFormUpdate(): Observable<FormGroup> {
    return this.currentUsanFormSubject.asObservable();
  }

  getCurrentUsanFormValue(): FormGroup {
    return this.currentUsanForm;
  }

  getCurrentUsanForm(formType: string): FormGroup {
    if (!this.currentUsanForm || this.currentUsanForm.get('formType')?.value !== formType) {
      this.createUsanForm(formType);
    }

    return this.getCurrentUsanFormValue();
  }

  getCurrentUsanFormType(): string {
    return this.getCurrentUsanFormValue().get('formType')?.value;
  }

  sendInvalidFormFieldsMessage(invalidFields: string[]) {
    this.invalidFields = invalidFields;
    this.invalidFormFieldsSubject.next(invalidFields);
  }
  
  getInvalidFormFieldsUpdate(): Observable<string[]> {
    return this.invalidFormFieldsSubject.asObservable();
  }

  public sendAllCaseAttachmentsUpdatedMessage(model: AllCaseAttachmentsModel) {
    this.allCaseAttachments = model;
    this.allCaseAttachmentsSubject.next({ value: model });
  }

  getAllCaseAttachmentsUpdate(): Observable<any> {
    return this.allCaseAttachmentsSubject.asObservable();
  }

  getAllCaseAttachmentsValue(): AllCaseAttachmentsModel {
    return this.allCaseAttachments;
  }

  public doesCurrentFormHaveAttachmentForSection(section: string): boolean {
    return this.allCaseAttachments?.hasAttachmentsForSection(section);
  }
  
  public allAttachmentsForSection(section: string): AttachmentModel[] {
    let attachments = this.allCaseAttachments?.getAttachments(section);
    return attachments ? attachments : [];
  }

  generateHeaderOptions() {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return options;
  }

  public createOrSaveCase(): Observable<any> {
    const form = this.getCurrentUsanFormValue();
    if(form?.get(`caseSysId`)?.value) {
      const req = this.formFactoryService.createSaveCaseRequestModel(form);
      return this.saveCase(req);
    } else {
      const req = this.formFactoryService.createCreateCaseRequestModel(form);
      return this.createCase(req).pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          console.error(error);
          return error;
        })
      );
    }
  }

  private createCase(model: CreateCaseRequestModel): Observable<FormGroup> {
    var formType = model?.u_form_type;
    if(!formType || !model) {
      throw new Error(`There was an error while attempting to save the form.`);
    }

    return this.http.post<CreateCaseResponse>(this.apiUrl + `/create-case/` + formType, model, this.generateHeaderOptions())
      .pipe(
        map(response => {
          const appInfo = ApplicationInfoModel.parseValues({
            formType: formType,
            status: 'draft',
            caseSysId: response?.Data?.result?.caseSysId
          });
          this.retrieveCaseAndSendFormUpdateMessage(appInfo, false);
          return this.getCurrentUsanFormValue();
        }),
        catchError(error => {
          console.error(error);
          return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
        })
      );
  }

  private saveCase(model: SaveCaseRequestModel): Observable<CaseResponseModel> {
    if(!model) {
      model = this.formFactoryService.createSaveCaseRequestModel(this.getCurrentUsanFormValue());
    }
    
    var formType = model?.u_form_type;
    var sysId = model?.sys_id;
    if(!formType || !model || !sysId) {
      return throwError(() => new Error(`There was an error while attempting to save the form.`));
    }
    return this.http.post<SaveCaseResponse>(this.apiUrl + `/update-case/` + formType + `/` + sysId, model)
    .pipe(
      map(response => {
        if(response?.Data?.result) {
          if(this.isShowingErrorSections) {
            this.forceValidateForm();
            this.invalidFormFields();
          }
          this.sendUsanFormUpdatedMessage(this.formFactoryService.createCaseFormGroup(response.Data.result));
        }
        return response?.Data?.result;
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
      })
    );

  }

  public unlockCase(unlockCaseModel?: UnlockCaseModel): Observable<any> {
    if(!unlockCaseModel && !this.getCurrentUsanFormValue()) {
      return of(false);
    }
    if(!unlockCaseModel) {
      unlockCaseModel = UnlockCaseModel.parseFormGroup(this.getCurrentUsanFormValue());
    }

    // Exiting an unsaved form.
    if(!unlockCaseModel?.caseSysId) {
      return of(true);
    }

    const lockedById = unlockCaseModel?.lockedById;

    if(lockedById && this.isFormLockedByDifferentUser(lockedById)) {
      return of(true);
    }

    const request = {
      caseSysId: unlockCaseModel?.caseSysId
    }

    return this.http.post<any>(this.apiUrl + `/unlock-case`, request, this.generateHeaderOptions());
  }

  public lockCase(appInfo: ApplicationInfoModel): Observable<any> {
    let modelInfo = {
      caseSysId: appInfo.caseSysId,
      u_form_type: appInfo.formType,
      sys_id: appInfo.formId,
      u_case_status: appInfo.status,
      u_locked_by_sso_id: this.userService.profile?.uniqueId // This won't actually save, the API will take care of this.
    };
    
    const model = SaveCaseRequestModel.parseValues(modelInfo);
    
    return this.saveCase(model).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.applicationLockedError));
      })
    );
  }
  
  private getCurrentDate(): number {
    return Date.now();
  }

  public isFormLocked(onlyByDifferentUser: boolean = true): boolean {
    const lockedById = this.getCurrentUsanFormValue()?.get('lockedById')?.value;
    if(!lockedById) {
      return false;
    }

    // No lock by user sso id or lock sso id is the same as the user's sso id (if onlyByDifferentUser is true), so the user can edit.
    if(onlyByDifferentUser && !this.isFormLockedByDifferentUser(lockedById)) {
      return false;
    }

    return this.isTimeStampLessThanLockTimeout(this.getFormLastLockedTime());
  }

  public isFormModelLocked(model: CaseFormModel): boolean {
    if(!this.isFormLockedByDifferentUser(model.u_locked_by_sso_id)) {
      return false;
    }

    const lastLockedTime = this.getFormModelLastLockedTime(model);
    return this.isTimeStampLessThanLockTimeout(lastLockedTime);
  }

  private isFormLockedByDifferentUser(lockedById: string): boolean {
    if(!lockedById?.trim()) {
      return false;
    }

    return lockedById.trim() !== this.userService.profile?.uniqueId;
  }

  public isTimeStampLessThanLockTimeout(lastLockedTime: number): boolean {
    const now = this.getCurrentDate();
    const diffInMs = Math.abs(now - lastLockedTime);
    const diffInMins = diffInMs / (1000 * 60); // MS -> Sec -> Mins
    return Math.abs(diffInMins) < 60;
  }

  private getFormLastLockedTime(): number {
    const lastLockedTimeObj = this.getCurrentUsanFormValue()?.get('lastLockedTime')?.value;

    if(!lastLockedTimeObj) {
      return 0;
    }

    const lastLockedTimeStr = String(lastLockedTimeObj);

    if(!lastLockedTimeStr?.trim()) {
      return 0;
    }
    
    return Number(lastLockedTimeStr);
  }

  private getFormModelLastLockedTime(model: CaseFormModel): number {
    if(!model.u_last_locked_activity_time?.trim()) {
      return 0;
    }

    const now = this.getCurrentDate();

    return Number(model.u_last_locked_activity_time.trim());
  }

  public isAppInfoModelLocked(model: ApplicationInfoModel) {
    // No lock by user sso id or lock sso id is the same as the user's sso id.
    if(!model.lockedById || model.lockedById === this.userService.profile?.uniqueId) {
      return false;
    }

    return this.isTimeStampLessThanLockTimeout(Number(model.lastLockedTime));
  }

  /**
   * 
   * @param doc The document to upload
   * @param documentName The name the document will be renamed to. This should be similar to the name of the upload section, (e.g. absence-of-conflict).
   * @returns 
   */
  public uploadFile(doc: File, documentName: string, formType: string, caseSysId: string) : Observable<any> { 
    if(!doc || !documentName || !formType || !caseSysId) {
      return throwError(() => new Error(`There was an error while attempting to upload the document.`));
    }
    
    const extnIdx = doc.name.lastIndexOf('.');
    const fileExtn = doc.name.slice(extnIdx);
    documentName = documentName + '_' + Date.now() + fileExtn;
    
    const formData = new FormData();
    formData.append('file', doc, documentName);
    
    return this.http.post<any>(this.apiUrl + `/upload/attachment/` + formType + `/` + caseSysId, formData)
    .pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
      })
    );
  }

  public updateWireTransferDataOnUploadProofOfPayment(amount: string | undefined) {
    //let paymentDate = moment(new Date()).format('MM/DD/YYYY');
    this.getCurrentUsanFormValue().patchValue({
      ['paymentType'] : 'WIRE',
      ['paymentStatus'] : 'PaymentComplete',
      ['paymentAmount'] : amount ? amount : '',
      ['orderId'] : this.getCurrentUsanFormValue().get('caseNumber')?.value,
      ['transactionId'] : null,
      ['authCode'] : null,
    });
    this.createOrSaveCase().subscribe();
  }

  public clearWireTransferDataOnDeleteProofOfPayment() {
    this.getCurrentUsanFormValue().patchValue({
      ['paymentType'] : '',
      ['paymentStatus'] : '',
      ['paymentAmount'] : '',
      ['orderId'] : '', 
      ['transactionId'] : '',
      ['authCode'] : ''
    });
    this.createOrSaveCase().subscribe();
  }

  public callFetchAttachmentsForCase(): Observable<AllCaseAttachmentsModel> {
    if(!this.currentUsanForm.get('formType')?.value || !this.currentUsanForm.get('sysId')?.value) {
      return throwError(() => new Error(`Cannot fetch file attachments due to missing parameter(s).`));
    }

    let req = {
      orgId: this.userService.profile.orgId,
      ssoId: this.userService.profile.uniqueId,
      email: this.userService.profile.email,
      status: [],
      formType: this.currentUsanForm.get('formType')?.value,
      caseSysId: this.currentUsanForm.get('caseSysId')?.value,
    };

    return this.fetchAttachments(FetchAttachmentsRequest.parseValues(req));
  }

  public fetchAttachments(request: FetchAttachmentsRequest): Observable<AllCaseAttachmentsModel> {
    if(!request.ssoId || !request.email || !request.formType || !request.caseSysId) {
        return throwError(() => new Error(`Cannot fetch file attachments due to missing parameter(s).`));
      }

      return this.http.post<FetchAttachmentsResponse>(this.apiUrl + `/attachments-case`, request, this.generateHeaderOptions())
      .pipe(
        map(response => {
          if(!response?.Data?.result) {
            return new AllCaseAttachmentsModel();
          }
          
          let model = AllCaseAttachmentsModel.mapAttachments(response.Data.result);
          this.sendAllCaseAttachmentsUpdatedMessage(model);
          return model;
        }),
        catchError(error => {
          console.error(error);
          return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
        })
      );
  } 

  public uploadFileAndRefreshAttachments(doc: File, documentName: string, formType?: string, caseSysId?: string): Observable<any> {
    let fType: string = '';
    let sId : string = '';
    if(this.currentUsanForm) {
      fType = this.currentUsanForm?.get('formType')?.value;
      sId = this.currentUsanForm?.get('caseSysId')?.value;
    } else if(formType && caseSysId) {
      fType = formType;
      sId = caseSysId;
    }
    return this.uploadFile(doc, documentName, fType, sId)
    .pipe(
      map(response => {
        let req = {
          orgId: this.userService.profile.orgId,
          ssoId: this.userService.profile.uniqueId,
          email: this.userService.profile.email,
          status: [],
         // formType: this.currentUsanForm?.get('formType')?.value,
         // caseSysId: this.currentUsanForm?.get('sysId')?.value,
          formType: fType,
          caseSysId: sId,
        };

        let attachmentsRequest = FetchAttachmentsRequest.parseValues(req);

        this.fetchAttachments(attachmentsRequest).subscribe();
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
      })
    );
  }

  public downloadAttachment(attachSysId: string): Observable<Blob>{
      let request = {
          'attachSysId' : attachSysId
      }
      const options = {
          responseType : 'blob' as 'json'
      }
      return this.http.post<Blob>(this.apiUrl + '/download/attachment', request,options)
  }

  public removeAttachment(attachmentModel: AttachmentModel): Observable<RemoveAttachmentResponse> {
    const request = RetrieveCaseByFiltersRequestModel.parseValues({
      attachSysId: attachmentModel.sys_id
    });
    
    const proofAttachments = this.allAttachmentsForSection(DocumentConstants.PROOF_OF_PAYMENT);
    if(proofAttachments?.length === 1) {
      this.clearWireTransferDataOnDeleteProofOfPayment();
    }

    return this.http.post<any>(this.apiUrl + `/attachment/remove`, request, this.generateHeaderOptions());
  }

  openCase(appInfo: ApplicationInfoModel): Observable<CaseResponse> {
    if(!appInfo || !appInfo.caseSysId) {
      return throwError(() => new Error(`There was an error while attempting to load the form data. Please try again.`));
    }

    const request = {
      caseSysId: appInfo.caseSysId,
      ssoId: this.userService.profile?.uniqueId
    };

    return this.http.post<any>(this.apiUrl + `/open-case`, request, this.generateHeaderOptions());
  }

  retrieveCase(appInfo: ApplicationInfoModel): Observable<CaseResponse> {
    if(!appInfo || !appInfo.formType || !appInfo.caseSysId) {
      return throwError(() => new Error(`There was an error while attempting to load the form data. Please try again.`));
    }

    const currentCaseSysId = this.getCurrentUsanFormValue()?.get('caseSysId')?.value;

    // Ensure we unlock the case that was loaded before loading another.
    if(currentCaseSysId && currentCaseSysId !== appInfo.caseSysId) {
      this.unlockCase().subscribe();
    }

    let formTypeParam = appInfo.formType;
    let formType = FormConstants.SERVICE_NOW_FORM_TYPE_MAP.get(formTypeParam);
    
    if(!formType) {
      formType = formTypeParam;
    }

    const request = RetrieveCaseByFiltersRequestModel.parseValues({
      formType: formType,
      caseSysId: appInfo.caseSysId
    });
    this.loadingForm = true;
    return this.http.post<any>(this.apiUrl + `/retrieve-case`, request, this.generateHeaderOptions())
    .pipe(
      map(response => {
        this.loadingForm = false;
        return response?.Data?.[0];
      }),
      catchError(error => {
        this.loadingForm = false;
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
      })
    );

  }

  refreshCurrentCaseData() {
    let info = {
      caseSysId: this.getCurrentUsanFormValue().get('caseSysId')?.value,
      formType: this.getCurrentUsanFormValue().get('formType')?.value,
      status: this.getCurrentUsanFormValue().get('caseStatus')?.value
    };
    let appInfo: ApplicationInfoModel = ApplicationInfoModel.parseValues(info);
    this.retrieveCaseAndSendFormUpdateMessage(appInfo, true);
  }

  retrieveCaseAndSendFormUpdateMessage(appInfo: ApplicationInfoModel, loadAttachments: boolean) {
    this.retrieveCase(appInfo).pipe(
      map(response => {
        let model = CaseFormModel.parseValues(response);
        model.caseSysId = appInfo.caseSysId;
        let formGroup = this.formFactoryService.createCaseFormGroup(model, model.u_form_type);
        this.sendUsanFormUpdatedMessage(formGroup);
        if(loadAttachments) {
          this.callFetchAttachmentsForCase().subscribe();
        }
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
      })
    ).subscribe();
  }

  loadAndViewCase(appInfo: ApplicationInfoModel, linkInfo: IAppFormLink) {
    this.hideErrorSections();
    this.lockCase(appInfo).pipe(
      map(response => {
        this.retrieveCase(appInfo).pipe(
          map(response => {
            let model = CaseFormModel.parseValues(response);
            model.caseSysId = appInfo.caseSysId;
            
            let formGroup = this.formFactoryService.createCaseFormGroup(model, model.u_form_type);
            this.sendUsanFormUpdatedMessage(formGroup);
            this.callFetchAttachmentsForCase().subscribe();
            this.router.navigate(linkInfo.formRoute.commands, linkInfo.formRoute.extras);
          }),
          catchError(error => {
            return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
          })
        ).subscribe();
      }),
      catchError(error => {
        console.error(error);
        this.router.navigate(['/usan/application'],  
          { queryParams : { formType: this.parseFormType(appInfo.formType), id: appInfo.caseSysId, source: 'applicant-dashboard-viewOnly'}});
        return of(false);
      })
    ).subscribe();
  }

  doesFormRequireProofOfPaymentUpload(): boolean {
    const paymentType = this.currentUsanForm.get('paymentType')?.value;
    
    // This is empty, so the Validator.Required will handle this case instead of handling it here.
    if(!paymentType || paymentType == null) {
      return false;
    }
    
    return paymentType === 'WIRE';
  }
  
  doesFormHaveProofOfPayment(): boolean {
    // Return true to prevent error from being shown due to missing proof of payment.
    if(!this.doesFormRequireProofOfPaymentUpload()) {
      return true;
    }

    return this.doesCurrentFormHaveAttachmentForSection(DocumentConstants.PROOF_OF_PAYMENT);
  }
  
  isPaymentCompleted(): boolean {
    let paymentType = this.getCurrentUsanFormValue().get('paymentType')?.value;
    let paymentStatus = this.getCurrentUsanFormValue().get('paymentStatus')?.value;
    return (paymentType != null && paymentStatus != null && paymentStatus == 'PaymentComplete');
  }

  submitForm(form?: FormGroup): Observable<CaseResponseModel> {
    if(!form) {
      form = this.currentUsanForm;
    }
    
    form.patchValue({
      'caseStatus' : 'Submitted',
      'submittedByFirstName' : this.userService.profile.firstName,
      'submittedByLastName' : this.userService.profile.lastName,
      'submittedDate' : new Date().toISOString().split('T')[0]
    });
    
    this.sendUsanFormUpdatedMessage(form);
    
    const req = this.formFactoryService.createSaveCaseRequestModel(form);
    return this.saveCase(req).pipe(
      map(response => {
        this.sendStatusChangeEmail(response).subscribe();
        this.unlockCase().subscribe();
        return response;
      }),
        catchError(error => {
          console.error(error);
          return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
        })
    );
  }

  sendStatusChangeEmail(model: CaseFormModel): Observable<boolean> {
    const changeStatusReq = {
      applicantEmail: model.u_usan_applicant_email,
      applicantName: model.u_usan_applicant_firstname + ' ' + model.u_usan_applicant_lastname,
      status: model.u_case_status, 
      fileNumber: model.caseNumber
    };

    return this.http.post<any>(this.apiUrl + `/notifications/email/change-status`, changeStatusReq, this.generateHeaderOptions())
      .pipe(
        map(response => {
          if(response.Data === `SUCCESS`) {
            return true;
          }
          return false;
        }),
        catchError(error => {
          console.error(error);
          return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
        })
      );
  }

  forceValidateForm() {
    this.updateTreeValidity(this.getCurrentUsanFormValue());
  }

  updateTreeValidity(group: FormGroup): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key];

      if (abstractControl instanceof FormGroup) {
        this.updateTreeValidity(abstractControl);
      } else {
        abstractControl.markAsTouched();
        abstractControl.markAsDirty();
        abstractControl.updateValueAndValidity();
      }
    });
  }

  public invalidFormFields() {
    let invalidFields: string[] = [];
    Object.keys(this.getCurrentUsanFormValue().controls).forEach(key => {
      const controlErrors: ValidationErrors | null | undefined = this.getCurrentUsanFormValue().get(key)?.errors;
      if (controlErrors != null && controlErrors != undefined) {
        invalidFields.push(key);
      }
    });

    this.sendInvalidFormFieldsMessage(invalidFields);
  }

  public isApplicantInfoPageValid(): boolean {
    let isValid = true;
    let applicantInfo: FormGroup | null = this.getCurrentUsanFormValue()?.get(`applicantInfo`) as FormGroup;
    if(applicantInfo) {
      Object.keys(applicantInfo.controls).forEach(key => {
        const controlErrors: ValidationErrors | null | undefined = applicantInfo?.get(key)?.errors;
        if(controlErrors != null && controlErrors != undefined) {
          isValid = false;
        }
      });
    } else {
      isValid = false;
    }

    return isValid;
  }

  get isLoadingForm(): boolean {
    return this.loadingForm;
  }

  get isShowingErrorSections(): boolean {
    return this.showingErrorSections;
  }

  public showErrorSections() {
    this.showingErrorSections = true;
  }

  public hideErrorSections() {
    this.showingErrorSections = false;
  }
  
  public hasFieldValidationError(fieldName: string, errorName?: string, formGroup?: string): boolean {
    let control;
    
    if(formGroup) {
      control = this.getCurrentUsanFormValue()?.get(formGroup)?.get(fieldName);
    } else {
      control = this.getCurrentUsanFormValue()?.get(fieldName);
    }

    if(!control) {
      return false;
    }

    if(errorName) {
      return (control.hasError(errorName) && this.shouldControlShowError(control));
    }

    return (!control.valid && this.shouldControlShowError(control));
  }

  private shouldControlShowError(control: AbstractControl) {
    return (this.isShowingErrorSections || (control.touched && control.dirty));
  }

  public retrieveOrgName() {
    const orgId = this.userService.profile.orgId;
    const uniqueId = this.userService.profile.uniqueId;
    const email = this.userService.profile.email;
    if(orgId) {
        this.retrieveOrgNameFromId(orgId, uniqueId, email).subscribe({
          next: res => {
            if(res.Data && res.Data.organizationName){
              //console.log('Retrieved org name '+ res.Data.organizationName)
              Object.assign(this.userService.profile, {orgName : res.Data.organizationName});
            }
          },
          error: error => {
            console.error(error);
          }
        });
    }
  }

  retrieveOrgNameFromId(orgId: string, uniqueId: string, email: string): Observable<ResultResponse> {
    let request = {
      'orgId' : orgId
    }
    let headers = {
      'x-mail': email,
      'x-amauniqueid' : uniqueId
    }
    return this.http.post<ResultResponse>(this.apiUrl + '/org/findbyid', request, {headers});         
  }

  parseFormType(str: string) : string {
    if(str.includes('Form '))
      return str.split('Form ')[1];
    else if(str.includes('Form'))
      return str.split('Form')[1];
    return str;
  }

  prepFormTypeForUpdateCase(str: string): string {
    if(!str) {
      return str;
    }
    const regex = /\s/g;
    str.replace(regex, '');
    if(str.length === 1) {
      str = `Form` + str;
    }
    return str;
  }
}
